package raycasting

import rendering.CameraManager
import rendering.RendererManager
import three.js.*
import util.MouseManager

object RaycastingManager {
    private val raycaster = Raycaster()

    private var lastX = -90000.0
    private var lastY = -90000.0

    /**
     * Update the raycaster according to the current mouse position.
     *
     * Caches the mouse position and only updates the intersection when it has changed.
     */
    // todo optimize for mobile devices -> also update mouse pos on pointer-down event?
    private fun updateCast() {
        if (lastX == MouseManager.mouseX && lastY == MouseManager.mouseY) {
            return
        }
        lastX = MouseManager.mouseX
        lastY = MouseManager.mouseY

        val rect = RendererManager.renderer.domElement.getBoundingClientRect()

        val x = (MouseManager.mouseX - rect.left) / rect.width * 2 - 1
        val y = -(MouseManager.mouseY - rect.top) / rect.height * 2 + 1
        val pointer = Vector2(x, y)

        raycaster.setFromCamera(pointer, CameraManager.camera)
    }

    /**
     * Pre-allocate the array of intersections to avoid creating a new one every time. Needs to be cleared
     * before using `raycaster.intersectObjects` by setting the javascript-specific length property to zero.
     */
    private val intersections = arrayOf<Intersection>()

    /**
     * Get the nearest of the objects that the raycaster intersects.
     */
    fun getFirstIntersection(from: Array<Object3D>): Intersection? {
        updateCast()
        intersections.asDynamic().length = 0
        raycaster.intersectObjects(from, true, intersections)
        return intersections.firstOrNull()
    }

    /**
     * Pre-allocate the vector for the intersection with a plane to avoid creating a new one every time.
     */
    private val planeIntersection = Vector3()

    /**
     * Get the intersection of the ray with the given plane.
     */
    fun intersectPlane(plane: Plane): Vector3 {
        updateCast()
        raycaster.ray.intersectPlane(plane, planeIntersection)
        return planeIntersection
    }
}